import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ServiceCard from '@/components/ServiceCard';
import { usePayment } from '@/hooks/usePayment';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { 
  Target, 
  Users, 
  Shield, 
  Star, 
  CheckCircle2, 
  Clock, 
  HeartHandshake, 
  Sparkles,
  FileText,
  Home,
  Globe2,
  Briefcase,
  Building2,
  ArrowRight,
  Zap,
  Award,
  Calculator
} from 'lucide-react';
import '@/styles/animations.css';

const Services = () => {
  const { createPayment, loading } = usePayment();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check for payment result in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    
    if (payment === 'success') {
      toast({
        title: "Payment Successful",
        description: "Your package has been activated successfully!",
      });
      navigate('/dashboard');
    } else if (payment === 'canceled') {
      toast({
        title: "Payment Canceled",
        description: "Your payment was canceled. You can try again anytime.",
        variant: "destructive",
      });
    }
  }, [navigate, toast]);

  // Main services with packages
  const mainServices = [
    {
      title: "Temporary Residence Card",
      description: "Legal residence in Poland for up to 3 years",
      icon: Clock,
      color: "from-blue-500/10 to-blue-600/10",
      iconColor: "text-blue-600"
    },
    {
      title: "Permanent Residence Card",
      description: "Secure your long-term future in Poland",
      icon: Home,
      color: "from-green-500/10 to-green-600/10",
      iconColor: "text-green-600"
    },
    {
      title: "EU Long-term Residence Card",
      description: "Freedom to live and work across the EU",
      icon: Globe2,
      color: "from-purple-500/10 to-purple-600/10",
      iconColor: "text-purple-600"
    }
  ];

  // Standalone services
  const standaloneServices = [
    {
      title: "Work Permit",
      description: "Fast-track your career in Poland",
      icon: Briefcase,
      color: "from-orange-500/10 to-orange-600/10",
      iconColor: "text-orange-600"
    },
    {
      title: "Register Your Company in Poland",
      description: "Transform your business idea into reality with our company registration service",
      icon: FileText,
      color: "from-indigo-500/10 to-indigo-600/10",
      iconColor: "text-indigo-600"
    },
    {
      title: "Book keeping",
      description: "Professional bookkeeping services for your business",
      icon: Calculator,
      color: "from-emerald-500/10 to-emerald-600/10",
      iconColor: "text-emerald-600"
    },
    {
      title: "Business Incubator",
      description: "Launch your business venture with comprehensive support and guidance",
      icon: Building2,
      color: "from-teal-500/10 to-teal-600/10",
      iconColor: "text-teal-600"
    },
    {
      title: "Translation Services",
      description: "Professional document translation for all your immigration needs",
      icon: FileText,
      color: "from-pink-500/10 to-pink-600/10",
      iconColor: "text-pink-600"
    }
  ];

  // Packages (keeping existing structure and prices)
  const packages = [
    {
      title: "Free Consultation",
      description: "Get started with basic guidance and document checklist",
      price: "Free",
      features: [
        "Initial consultation call",
        "Basic document checklist", 
        "General guidance",
        "Email support"
      ],
      icon: HeartHandshake,
      isFree: true,
      onSelect: () => {
        if (!user) {
          navigate('/register');
          return;
        }
        navigate('/consultation');
      }
    },
    {
      title: "Basic Package",
      description: "Essential support for your Polish residence card application",
      price: "1299 PLN",
      features: [
        "Document review and feedback",
        "Application form assistance", 
        "Email and phone support",
        "Basic appointment scheduling help"
      ],
      icon: CheckCircle2,
      onSelect: () => {
        if (!user) {
          navigate('/register');
          return;
        }
        createPayment('basic');
      }
    },
    {
      title: "Standard Package", 
      description: "Comprehensive application support with priority assistance",
      price: "2199 PLN",
      features: [
        "Full application review",
        "Document preparation assistance",
        "Priority support",
        "Appointment booking assistance", 
        "Interview preparation"
      ],
      icon: Clock,
      popular: true,
      onSelect: () => {
        if (!user) {
          navigate('/register');
          return;
        }
        createPayment('standard');
      }
    },
    {
      title: "Premium Package",
      description: "Complete white-glove service with personal consultant",
      price: "3499 PLN", 
      features: [
        "End-to-end application management",
        "Personal immigration consultant",
        "24/7 priority support",
        "Document translation service",
        "Follow-up and status tracking",
        "Success guarantee"
      ],
      icon: Sparkles,
      onSelect: () => {
        if (!user) {
          navigate('/register');
          return;
        }
        createPayment('premium');
      }
    }
  ];

  const stats = [
    { number: "98%", label: "Success Rate", icon: Target },
    { number: "1000+", label: "Happy Clients", icon: Users },
    { number: "5+", label: "Years Experience", icon: Shield },
    { number: "24/7", label: "Support", icon: Star }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-primary/10 to-background py-24 sm:py-60">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              Immigration Services
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up" style={{ animationDelay: '200ms' }}>
              Your gateway to Poland. Comprehensive immigration services to help you establish your life and business in Poland.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Main Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-slide-in-bottom">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Zap className="h-4 w-4" />
              Choose Your Immigration Path
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Our services come with flexible support packages to match your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 justify-center max-w-5xl mx-auto">
            {mainServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={index} 
                  className="animate-slide-in-bottom group cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => {
                    if (service.title === "Temporary Residence Card") {
                      navigate('/temporary-residence-card');
                    } else if (service.title === "Permanent Residence Card") {
                      navigate('/permanent-residence-card');
                    } else if (service.title === "EU Long-term Residence Card") {
                      navigate('/eu-long-term-residence-card');
                    } else {
                      const packagesSection = document.getElementById('packages-section');
                      packagesSection?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  <div className={`bg-gradient-to-br ${service.color} rounded-2xl p-6 h-full backdrop-blur-sm border border-border/50 group-hover:scale-105 transition-all duration-300 hover:shadow-lg`}>
                    <div className="bg-white/20 p-3 rounded-full w-12 h-12 mb-4 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <IconComponent className={`h-6 w-6 ${service.iconColor}`} />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2 font-alumni">{service.title}</h3>
                    <p className="text-muted-foreground text-sm font-hind">{service.description}</p>
                    <div className="flex items-center gap-2 mt-4 text-primary font-medium text-sm">
                      <span>Read more</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Packages Section */}
        <div id="packages-section" className="mb-20">
          <div className="text-center mb-12 animate-slide-in-bottom">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="h-4 w-4" />
              Support Packages
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Select the perfect package for your application
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {packages.map((package_, index) => (
              <div key={index} className="animate-slide-in-bottom" style={{ animationDelay: `${(index + 1) * 150}ms` }}>
                <ServiceCard {...package_} />
              </div>
            ))}
          </div>
        </div>

        {/* Standalone Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-slide-in-bottom">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Star className="h-4 w-4" />
              Additional Services
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Specialized services for specific needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {standaloneServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={index} 
                  className="animate-slide-in-bottom group cursor-pointer"
                  style={{ animationDelay: `${index * 150}ms` }}
                  onClick={() => {
                    if (service.title === "Work Permit") {
                      navigate('/work-permit');
                    } else if (service.title === "Register Your Company in Poland") {
                      navigate('/company-registration');
                    } else if (service.title === "Book keeping") {
                      navigate('/book-keeping');
                    } else if (service.title === "Business Incubator") {
                      navigate('/business-incubator');
                    } else if (service.title === "Translation Services") {
                      navigate('/translation-services');
                    } else {
                      navigate('/contact');
                    }
                  }}
                >
                  <div className={`bg-gradient-to-br ${service.color} rounded-2xl p-8 h-full backdrop-blur-sm border border-border/50 group-hover:scale-105 transition-all duration-300 hover:shadow-lg`}>
                    <div className="bg-white/20 p-4 rounded-full w-16 h-16 mb-6 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <IconComponent className={`h-8 w-8 ${service.iconColor}`} />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">{service.title}</h3>
                    <p className="text-muted-foreground font-hind leading-relaxed mb-6">{service.description}</p>
                    <div className="flex items-center gap-2 text-primary font-medium text-sm">
                      <span>Read more</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-card rounded-2xl shadow-sm p-12 mb-20 border border-border overflow-hidden backdrop-blur-sm">
          <div className="text-center mb-12 animate-slide-in-bottom">
            <h2 className="text-4xl font-bold text-foreground mb-6 font-alumni">
              Why Choose Sigma Immigration?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Our expert team has successfully helped thousands of clients establish their business and immigration presence in Poland
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="animate-slide-in-bottom" style={{ animationDelay: '900ms' }}>
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 h-full backdrop-blur-sm group hover:scale-105 transition-transform duration-300">
                <div className="bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center animate-float">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 text-center font-alumni">Expert Guidance</h3>
                <p className="text-muted-foreground text-center font-hind">
                  Our team has successfully helped thousands of clients establish their business and immigration presence in Poland
                </p>
              </div>
            </div>
            <div className="animate-slide-in-bottom" style={{ animationDelay: '1000ms' }}>
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 h-full backdrop-blur-sm group hover:scale-105 transition-transform duration-300">
                <div className="bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center animate-float">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 text-center font-alumni">Fast Processing</h3>
                <p className="text-muted-foreground text-center font-hind">
                  Streamlined process to minimize delays and get your application submitted quickly
                </p>
              </div>
            </div>
            <div className="animate-slide-in-bottom" style={{ animationDelay: '1100ms' }}>
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 h-full backdrop-blur-sm group hover:scale-105 transition-transform duration-300">
                <div className="bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center animate-float">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 text-center font-alumni">High Success Rate</h3>
                <p className="text-muted-foreground text-center font-hind">
                  95%+ approval rate for applications we handle from start to finish
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-primary/5 rounded-2xl p-12 mb-20 animate-fade-in backdrop-blur-sm" style={{ animationDelay: '800ms' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div 
                  key={index}
                  className="animate-slide-in-bottom group"
                  style={{ animationDelay: `${1000 + index * 100}ms` }}
                >
                  <div className="mb-4 flex justify-center">
                    <div className="bg-primary/10 p-3 rounded-full animate-pulse-soft">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform">
                    {stat.number}
                  </div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Services;
